import React, { useRef } from 'react';
import styled from 'styled-components';
import { Container } from 'react-bootstrap';
import { device } from '../utils/breakpoints';

const TrustedSection = styled.section`
  background: #ffffff;
  padding: 80px 0;
  border-bottom: 1px solid #E5E7EB;
  
  @media ${device.tablet} {
    padding: 60px 0;
  }
`;

const SectionTitle = styled.h3`
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: #000000;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-bottom: 50px;
`;

const CarouselContainer = styled.div`
  position: relative;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 60px;
  
  @media ${device.tablet} {
    padding: 0 50px;
  }
`;

const CarouselWrapper = styled.div`
  overflow: hidden;
  position: relative;
  
  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 60px;
    z-index: 2;
    pointer-events: none;
  }
  
  &::before {
    left: 0;
    background: linear-gradient(to right, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0));
  }
  
  &::after {
    right: 0;
    background: linear-gradient(to left, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0));
  }
`;

const ScrollButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #ffffff;
  border: 1px solid #E5E7EB;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 3;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: #3B82F6;
    border-color: #3B82F6;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
    
    &::after {
      border-color: #ffffff;
    }
  }
  
  &:active {
    transform: translateY(-50%) scale(0.95);
  }
  
  &::after {
    content: '';
    width: 8px;
    height: 8px;
    border-top: 2px solid #000000;
    border-right: 2px solid #000000;
    transition: border-color 0.3s ease;
  }
  
  @media ${device.tablet} {
    width: 36px;
    height: 36px;
  }
`;

const ScrollButtonLeft = styled(ScrollButton)`
  left: 0;
  
  &::after {
    transform: rotate(-135deg);
    margin-left: 3px;
  }
`;

const ScrollButtonRight = styled(ScrollButton)`
  right: 0;
  
  &::after {
    transform: rotate(45deg);
    margin-right: 3px;
  }
`;

const IndustriesCarousel = styled.div`
  display: flex;
  gap: 24px;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 20px 0;
  
  /* Hide scrollbar but keep functionality */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;

const IndustryCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 40px;
  background: #F9FAFB;
  border-radius: 12px;
  border: 1px solid #E5E7EB;
  transition: all 0.3s ease;
  cursor: default;
  flex-shrink: 0;
  min-width: 180px;
  
  &:hover {
    background: #ffffff;
    border-color: #3B82F6;
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(59, 130, 246, 0.1);
  }
`;

const IndustryIcon = styled.div`
  font-size: 40px;
  margin-bottom: 16px;
  filter: grayscale(30%);
  transition: filter 0.3s ease;
  
  ${IndustryCard}:hover & {
    filter: grayscale(0%);
  }
`;

const IndustryName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #000000;
  text-align: center;
`;

const TrustedBy = () => {
  const carouselRef = useRef(null);
  
  const industries = [
    { icon: '🏥', name: 'Healthcare' },
    { icon: '🏦', name: 'Finance' },
    { icon: '🎓', name: 'Education' },
    { icon: '🏛️', name: 'Government' },
    { icon: '🏭', name: 'Manufacturing' },
    { icon: '🛒', name: 'Retail' },
    { icon: '🚀', name: 'Technology' },
    { icon: '⚖️', name: 'Legal' }
  ];

  const scroll = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = 300;
      const newPosition = direction === 'left' 
        ? carouselRef.current.scrollLeft - scrollAmount
        : carouselRef.current.scrollLeft + scrollAmount;
      
      carouselRef.current.scrollTo({
        left: newPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <TrustedSection>
      <Container>
        <SectionTitle>Trusted by Industries</SectionTitle>
      </Container>
      <CarouselContainer>
        <ScrollButtonLeft onClick={() => scroll('left')} aria-label="Scroll left" />
        <CarouselWrapper>
          <IndustriesCarousel ref={carouselRef}>
            {industries.map((industry, index) => (
              <IndustryCard key={index}>
                <IndustryIcon>{industry.icon}</IndustryIcon>
                <IndustryName>{industry.name}</IndustryName>
              </IndustryCard>
            ))}
            {/* Duplicate for seamless scrolling */}
            {industries.map((industry, index) => (
              <IndustryCard key={`duplicate-${index}`}>
                <IndustryIcon>{industry.icon}</IndustryIcon>
                <IndustryName>{industry.name}</IndustryName>
              </IndustryCard>
            ))}
          </IndustriesCarousel>
        </CarouselWrapper>
        <ScrollButtonRight onClick={() => scroll('right')} aria-label="Scroll right" />
      </CarouselContainer>
    </TrustedSection>
  );
};

export default TrustedBy;


